import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/axios';

export type ModuleSource =
  | 'owner'
  | 'workspace_override'
  | 'org_override'
  | 'plan'
  | 'role_restricted'
  | 'user_override'
  | 'disabled';

export interface ModulePermissions {
  view: boolean;
  edit: boolean;
  create: boolean;
  delete: boolean;
  admin: boolean;
}

export interface EffectiveModule {
  module_id: string;
  enabled: boolean;
  source: ModuleSource;
  permissions: ModulePermissions;
}

/**
 * WorkspaceEntitlements Model
 * 
 * This is the canonical structure for workspace entitlements.
 * Backend API should return data in this format.
 * 
 * TODO: When backend API is ready, update the API response mapping to match this structure.
 */
export interface WorkspaceEntitlements {
  modules: string[]; // Array of enabled module IDs
  permissions: string[]; // Array of permission strings (e.g., ['view:bills', 'edit:suppliers'])
  plan: {
    code: string;
    name: string;
  } | null;
}

/**
 * Static Fallback Entitlements
 * 
 * Used when backend doesn't return entitlements yet.
 * Provides a safe default that allows core functionality.
 */
const FALLBACK_ENTITLEMENTS: WorkspaceEntitlements = {
  modules: [
    'bill_collection',
    'bill_processing',
    'onboarding',
    'reporting',
    'analytics',
  ],
  permissions: [
    'view:bills',
    'view:suppliers',
    'view:customers',
    'view:reports',
    'edit:bills',
    'edit:suppliers',
  ],
  plan: {
    code: 'starter',
    name: 'Starter Plan',
  },
};

/**
 * All Available Modules
 * 
 * Complete list of modules in the system.
 * Used to determine if a module exists and for owner entitlements.
 */
const ALL_MODULES = [
  'bill_collection',
  'bill_processing',
  'onboarding',
  'ai_parser',
  'ai_agents',
  'automation',
  'reporting',
  'analytics',
  'enterprise_analytics',
  'compliance',
  'integrations',
  'api',
  'storage',
  'support',
  'trial_management',
  'workspace_admin',
  'bulk_operations',
  'audit_logs',
  'security_monitoring',
  'role_management',
  'multi_tenant',
] as const;

export interface UseModuleAccess {
  isOwner: boolean;
  userRole: string;
  planCode?: string;
  planName?: string;
  enabledModules: string[];
  enabledModulesCount: number;
  modules: EffectiveModule[];
  hasModule: (moduleId: string) => boolean;
  hasPermission: (
    moduleId: string,
    action: 'view' | 'edit' | 'create' | 'delete' | 'admin'
  ) => boolean;
  isLoading: boolean;
  error?: Error | null;
  allModules: EffectiveModule[];
}

/**
 * 🔐 useModuleAccess
 * 
 * Stable entitlement-based architecture for module and permission checking.
 * 
 * Architecture:
 * - Uses WorkspaceEntitlements model for consistent data structure
 * - Provides static fallback when backend data unavailable
 * - Determines owner status from user role
 * - hasModule() and hasPermission() use real entitlements (not always true)
 * 
 * Backend Integration:
 * TODO: When backend API is ready, update queryFn to call:
 *   const res = await api.get(`/${scope}s/${id}/entitlements`);
 *   return res.data; // Should match WorkspaceEntitlements structure
 * 
 * HARD GUARANTEES:
 * - All hooks called unconditionally (Rules of Hooks)
 * - Always returns stable object shape
 * - Never crashes on undefined arrays
 * - Owner is ALWAYS allowed (determined from user.role)
 * - Arrays are always defined (never undefined)
 */
export const useModuleAccess = (
  scope: 'workspace' | 'organization',
  id?: string
): UseModuleAccess => {
  const { user } = useAuth();

  /**
   * 🎣 HOOKS MUST BE CALLED UNCONDITIONALLY
   * 
   * Rules of Hooks: All hooks must be called in the same order every render.
   * We control execution via `enabled` flag, not early returns.
   */
  const query = useQuery({
    queryKey: ['entitlements', scope, id],
    enabled: !!id, // Only fetch when id is available
    retry: false,
    staleTime: 60 * 1000,
    queryFn: async () => {
      // Call backend API to fetch entitlements
      // Expected endpoint: GET /workspaces/:id/entitlements or /organizations/:id/entitlements
      // Expected response format: WorkspaceEntitlements
      try {
        const res = await api.get(`/${scope}s/${id}/entitlements`);
        return res.data as WorkspaceEntitlements;
      } catch (error: any) {
        // If endpoint doesn't exist yet or returns 404, return null to trigger fallback
        if (error.response?.status === 404) {
          console.warn(`Entitlements endpoint not available for ${scope} ${id}, using fallback`);
          return null;
        }
        // For other errors, throw to let React Query handle retry/error state
        throw error;
      }
    },
  });

  /**
   * 🧠 COMPUTE ENTITLEMENTS
   * 
   * Uses backend data if available, otherwise falls back to static entitlements.
   * Owner status is determined from user role, not backend response.
   */
  const isOwner = useMemo(() => {
    return user?.role === 'owner' || user?.role === 'super_admin';
  }, [user?.role]);

  const userRole = useMemo(() => {
    return user?.role || 'viewer';
  }, [user?.role]);

  /**
   * 🛡️ ENTITLEMENTS RESOLUTION
   * 
   * Priority:
   * 1. Backend response (if available)
   * 2. Fallback entitlements (if backend unavailable)
   * 3. Owner gets all modules (computed in hasModule/hasPermission)
   */
  const entitlements = useMemo((): WorkspaceEntitlements => {
    // If backend returned data, use it
    if (query.data) {
      return {
        modules: Array.isArray(query.data.modules) ? query.data.modules : [],
        permissions: Array.isArray(query.data.permissions) ? query.data.permissions : [],
        plan: query.data.plan || null,
      };
    }

    // Otherwise, use fallback entitlements
    return FALLBACK_ENTITLEMENTS;
  }, [query.data]);

  /**
   * 📦 COMPUTE ENABLED MODULES
   * 
   * Owners get all modules. Non-owners get modules from entitlements.
   */
  const enabledModules = useMemo(() => {
    if (isOwner) {
      // Owner gets all modules
      return [...ALL_MODULES];
    }
    
    // Non-owners get modules from entitlements
    return Array.isArray(entitlements.modules) ? entitlements.modules : [];
  }, [isOwner, entitlements.modules]);

  /**
   * 📋 COMPUTE EFFECTIVE MODULES
   * 
   * Transforms entitlements into EffectiveModule format for compatibility.
   */
  const modules: EffectiveModule[] = useMemo(() => {
    return enabledModules.map((moduleId) => ({
      module_id: moduleId,
      enabled: true,
      source: isOwner ? ('owner' as ModuleSource) : ('plan' as ModuleSource),
      permissions: {
        view: true,
        edit: isOwner,
        create: isOwner,
        delete: isOwner,
        admin: isOwner,
      },
    }));
  }, [enabledModules, isOwner]);

  /**
   * 🔍 MODULE CHECK
   * 
   * Checks if a module is enabled based on entitlements.
   * Owners always have access to all modules.
   */
  const hasModule = useMemo(() => {
    return (moduleId: string): boolean => {
      // Owner bypass - always allow
      if (isOwner) return true;
      
      // Check if module is in enabled modules list
      return enabledModules.includes(moduleId);
    };
  }, [isOwner, enabledModules]);

  /**
   * 🔐 PERMISSION CHECK
   * 
   * Checks if user has a specific permission for a module.
   * Owners always have all permissions.
   * 
   * TODO: When backend provides granular permissions, enhance this logic:
   * - Check entitlements.permissions array for specific permission strings
   * - Support permission format: 'action:resource' (e.g., 'edit:bills', 'view:suppliers')
   */
  const hasPermission = useMemo(() => {
    return (
      moduleId: string,
      action: 'view' | 'edit' | 'create' | 'delete' | 'admin'
    ): boolean => {
      // Owner bypass - always allow
      if (isOwner) return true;
      
      // Check if module is enabled (basic permission check)
      if (!hasModule(moduleId)) return false;
      
      // TODO: When backend provides granular permissions, check entitlements.permissions:
      // const permissionString = `${action}:${moduleId}`;
      // return entitlements.permissions.includes(permissionString);
      
      // For now, if module is enabled, allow view permission
      // Other permissions require owner status (handled above)
      if (action === 'view') return true;
      
      // Non-owners don't have edit/create/delete/admin permissions (unless backend says otherwise)
      return false;
    };
  }, [isOwner, hasModule]);

  // Safely compute enabled modules count
  const enabledModulesCount = enabledModules.length;

  return {
    isOwner,
    userRole,
    planCode: entitlements.plan?.code,
    planName: entitlements.plan?.name,
    enabledModules, // Always defined array
    enabledModulesCount, // Always a number
    modules, // Always defined array
    allModules: modules, // Always defined array
    hasModule,
    hasPermission,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
};
